import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, MessageCircle, Send, X } from "lucide-react";
import { api } from "@/lib/api";

const CAT_STYLE = {
  booking_paid: { dot: "bg-emerald-500", label: "Booking berbayar" },
  upcoming: { dot: "bg-sky-500", label: "Jadwal hari ini" },
  return_due: { dot: "bg-amber-500", label: "Jatuh tempo kembali" },
  overdue: { dot: "bg-lm-red", label: "Telat kembali" },
  inspection: { dot: "bg-violet-500", label: "Perlu inspeksi" },
  damage: { dot: "bg-lm-red", label: "Kerusakan" },
  payment_exception: { dot: "bg-lm-red", label: "Masalah pembayaran" },
  cancellation: { dot: "bg-amber-600", label: "Pembatalan" },
  hub_conflict: { dot: "bg-lm-red", label: "Konflik Hub" },
  commission: { dot: "bg-emerald-600", label: "Komisi" },
  finance_task: { dot: "bg-emerald-600", label: "Refund / deposit" },
  system: { dot: "bg-ink", label: "Sistem" },
};
const KIND_STYLE = {
  "payment.received": { dot: "bg-emerald-500", label: "Pembayaran" },
  "booking.created": { dot: "bg-ink", label: "Booking baru" },
  "return.overdue": { dot: "bg-lm-red", label: "Telat kembali" },
};

export const NotificationBell = ({ variant = "" }) => {
  const tid = (name) => `notif-${name}${variant ? `-${variant}` : ""}`;
  const isMobile = variant === "mobile";
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState([]);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const box = useRef(null);

  const loadCount = useCallback(() => api.get("/notifications/unread-count")
    .then((r) => setUnread(r.data.unread)).catch(() => {}), []);
  const loadRows = useCallback(() => api.get("/notifications", {
    params: { limit: 30, unread_only: onlyUnread || undefined },
  }).then((r) => setRows(r.data)).catch(() => {}), [onlyUnread]);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);
  useEffect(() => { if (open) loadRows(); }, [open, loadRows]);
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setRows((p) => p.map((r) => (r.id === id ? { ...r, read: true } : r)));
    loadCount();
  };
  const markAll = async () => { await api.post("/notifications/read-all"); loadRows(); loadCount(); };

  return (
    <div className="relative" ref={box}>
      <button data-testid={tid("bell")} onClick={() => setOpen(!open)}
        className="relative flex items-center gap-2 px-2 py-2 opacity-70 hover:opacity-100 transition-opacity">
        <Bell size={16} />
        {!isMobile && <span className="text-xs">Notifikasi</span>}
        {unread > 0 && (
          <span data-testid={tid("badge")}
            className="absolute -top-0.5 left-3 min-w-[16px] h-4 px-1 rounded-full bg-lm-red text-white text-[10px] font-semibold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid={tid("panel")}
          className={`absolute w-[min(340px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto bg-white text-ink rounded-lg shadow-2xl border border-black/10 z-50 ${
            isMobile ? "top-full mt-2 right-0" : "bottom-full mb-2 left-0"}`}>
          <div className="sticky top-0 bg-white/95 backdrop-blur px-4 py-3 border-b border-black/5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Notifikasi</span>
              <div className="flex items-center gap-2">
                <button data-testid={tid("mark-all")} onClick={markAll}
                  className="text-[11px] text-ink-soft hover:text-lm-red">Tandai semua</button>
                <button onClick={() => setOpen(false)} className="text-ink-soft hover:text-ink"><X size={14} /></button>
              </div>
            </div>
            <div className="mt-2 flex gap-1.5">
              {[["Semua", false], ["Belum dibaca", true]].map(([label, val]) => (
                <button key={label} data-testid={tid(val ? "filter-unread" : "filter-all")}
                  onClick={() => setOnlyUnread(val)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${
                    onlyUnread === val ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {rows.length === 0 && (
            <div className="px-4 py-6 text-xs text-ink-soft" data-testid={tid("empty")}>
              Belum ada notifikasi. Booking berbayar, jatuh tempo kembali, telat, kerusakan dan
              masalah pembayaran akan muncul di sini.
            </div>
          )}
          <div className="divide-y divide-black/5">
            {rows.map((n) => {
              const st = CAT_STYLE[n.category] || KIND_STYLE[n.kind] || { dot: "bg-ink", label: n.kind };
              return (
                <div key={n.id} data-testid={`notif-item-${n.id}`}
                  className={`px-4 py-3 ${n.read ? "" : "bg-cream-dark/50"}`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${st.dot}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold break-words">{n.title}</div>
                      <div className="text-[11px] text-ink-soft break-words">{n.body}</div>
                      <div className="text-[10px] text-ink-soft mt-0.5">
                        {n.at_jkt} WIB · {st.label}
                        {n.priority === "high" ? " · penting" : ""}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {n.wa_owner_link && (
                          <a data-testid={`notif-wa-owner-${n.id}`} href={n.wa_owner_link} target="_blank" rel="noreferrer"
                            className="text-[11px] px-2 py-1 rounded border border-black/15 flex items-center gap-1 hover:border-lm-red">
                            <Send size={10} /> WA ke saya
                          </a>
                        )}
                        {n.wa_customer_link && (
                          <a data-testid={`notif-wa-customer-${n.id}`} href={n.wa_customer_link} target="_blank" rel="noreferrer"
                            className="text-[11px] px-2 py-1 rounded border border-black/15 flex items-center gap-1 hover:border-lm-red">
                            <MessageCircle size={10} /> Balas customer
                          </a>
                        )}
                        {(n.action_url || n.entity_id) && (
                          <Link to={n.action_url || `/admin/bookings/${n.entity_id}`}
                            onClick={() => setOpen(false)} data-testid={`notif-open-${n.id}`}
                            className="text-[11px] px-2 py-1 rounded border border-black/15 hover:border-lm-red">
                            {n.action_label || "Buka booking"}
                          </Link>
                        )}
                        {!n.read && (
                          <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n.id)}
                            className="text-[11px] text-ink-soft hover:text-lm-red flex items-center gap-1">
                            <Check size={10} /> Dibaca
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
